'use client';

import { Link } from 'react-router-dom';
import { JsonLd } from '@/seo/JsonLd';
import type { Product } from '@/data/products';
import { products } from '@/data/products';
import { ProductCard } from '@/components/ProductCard/ProductCard';
import { Accordion } from '@/components/Accordion/Accordion';
import { Tabs } from '@/components/Tabs/Tabs';
import { IconWhatsApp, IconPhone, IconStar } from '@/components/Icons/Icons';
import { buildWhatsAppUrl, SALES_PHONE_READABLE } from '@/utils/whatsapp';
import { SITE_DOMAIN, toAbsoluteUrl } from '@/utils/url';
import styles from './ProductDetail.module.css';

const izmirProduct: Product = {
  slug: 'izmir-bondex-sunger',
  name: 'İzmir Bondex Sünger',
  category: 'Bondex Sünger',
  shortDescription:
    'İzmir bondex sünger; şehir içi hızlı teslimat, proje bazlı keşif ve akustik danışmanlıkla rebonded süngerpan tedariki sunar.',
  description:
    'İzmir bondex sünger; yüksek yoğunluklu rebonded süngerpan yapısıyla darbe ve titreşim seslerini azaltmaya yardımcı olan, İzmir içi projelere özel kurgulanmış bir ses yalıtım süngeri çözümüdür. Konut, ofis, stüdyo ve endüstriyel projelerde güvenle kullanılır.',
  imagePath: '/images/bondex-4.webp',
  sku: 'BDX-IZM',
  mpn: 'BDX-IZM',
  priceTRY: 130,
  priceUnit: 'm2',
  faqs: [
    {
      question: 'İzmir bondex sünger nedir?',
      answer:
        'İzmir bondex sünger; rebonded süngerpan yapısına sahip, İzmir içi projelere özel planlanan yüksek yoğunluklu bir ses yalıtım ve titreşim sönümleme çözümüdür. Konut, ofis, stüdyo, otel ve endüstriyel alanlarda kullanılır.',
    },
    {
      question: 'İzmir içinde bondex sünger teslimat süresi nedir?',
      answer:
        'Stoktan sevk edilen İzmir bondex sünger siparişleri, İzmir merkez ve çevre ilçelerde çoğunlukla 1-3 iş günü içinde teslim edilir. Acil projelerde aynı gün planlama ve etaplı sevkiyat seçenekleri proje durumuna göre organize edilir.',
    },
    {
      question: 'İzmir bondex sünger hangi kalınlıklarda var?',
      answer:
        "İzmir için ana stok kalınlıkları 10mm, 20mm, 30mm, 40mm ve 50mm'dir. Hafif ses geçişlerinde 10-20mm, daha yüksek izolasyon hedeflerinde 30-50mm; ileri performans gereken projelerde bariyerli ve çift bariyerli sistemler önerilir.",
    },
    {
      question: "İzmir'de keşif veya uygulama desteği veriyor musunuz?",
      answer:
        'İzmir içi projelerde yerinde keşif, katman kurgusu, metraj doğrulama ve uygulama ekibi yönlendirmesi sağlanabilir. Projenin kullanım tipi, hedef izolasyon seviyesi ve bütçesine göre en uygun uygulama planı oluşturulur.',
    },
    {
      question: 'İzmir bondex sünger fiyatı ne kadar?',
      answer:
        "İzmir bondex sünger fiyatı metrekare (m²) başına 130 TL'den başlar. Nihai fiyat; kalınlık, toplam metraj, katman kombinasyonu ve teslimat planına göre değişir. Hızlı ve net teklif için proje detaylarınızı WhatsApp üzerinden iletebilirsiniz.",
    },
  ],
  reviews: [
    {
      authorName: 'Eren Yıldırım',
      datePublished: '2026-01-06',
      ratingValue: 5,
      title: 'Bornova konut projesinde net fark',
      body:
        "İzmir Bornova'daki konut projemizde zemin şap altında bondex sünger kullandık. Darbe sesi ciddi şekilde azaldı, ekip metraj ve kalınlık seçiminde çok doğru yönlendirme yaptı.",
      verified: true,
    },
    {
      authorName: 'Mert Kaya',
      datePublished: '2026-01-09',
      ratingValue: 5,
      title: 'Alsancak ofisinde konfor arttı',
      body:
        "Alsancak'taki ofis katımızda duvar ve teknik hacimlerde İzmir bondex sünger uygulandı. Ortam gürültüsü düşerken toplantı odalarında ses kalitesi belirgin şekilde iyileşti.",
      verified: true,
    },
    {
      authorName: 'Selin Arslan',
      datePublished: '2026-01-13',
      ratingValue: 4,
      title: 'Stüdyo için dengeli çözüm',
      body:
        "Karşıyaka'daki kayıt odamızda bondex sünger ile bariyerli katmanı birlikte kullandık. Dışarıya ses kaçağı azaldı, içeride daha kontrollü ve temiz bir akustik elde ettik.",
      verified: true,
    },
    {
      authorName: 'Onur Demir',
      datePublished: '2026-01-16',
      ratingValue: 5,
      title: 'Makine odasında başarılı sonuç',
      body:
        "Çiğli'deki üretim tesisimizde makine odasında bondex ve bariyerli sistem uygulandı. Titreşim ve ses şikayetleri önemli ölçüde azaldı, uygulama planı hızlı ilerledi.",
      verified: true,
    },
    {
      authorName: 'Gizem Şahin',
      datePublished: '2026-01-21',
      ratingValue: 5,
      title: 'Hızlı teslimat, temiz işçilik',
      body:
        "Buca'daki apartman yenileme projemiz için 150 m² ürün sipariş ettik. İzmir içi teslimat kısa sürede geldi, kesim kalitesi ve uygulama disiplini beklentimizin üstündeydi.",
      verified: true,
    },
  ],
};

export function CityBondexIzmir() {
  const url = toAbsoluteUrl('/izmir-bondex-sunger');
  const title = 'İzmir Bondex Sünger | Yalıtım Süngeri– Echopan';
  const description =
    'İzmir bondex sünger, yüksek yoğunluklu yapısıyla darbe ve titreşim seslerini azaltır. Akustik yalıtım süngeri için üretici Echopan kalitesiyle sunulur.';

  const relatedSlugs = [
    'bondex-sunger',
    'bondex-sunger-10mm',
    'bondex-sunger-20mm',
    'bondex-sunger-30mm',
    'bondex-sunger-40mm',
    'bariyerli-bondex-sunger',
    'cift-bariyerli-bondex-sunger',
  ];

  const relatedBondex = relatedSlugs
    .map((slug) => products.find((p) => p.slug === slug))
    .filter((p): p is Product => Boolean(p));

  const waMessage = 'Merhaba, İzmir bondex sünger için fiyat ve teslimat bilgisi rica ediyorum. (izmir-bondex-sunger)';
  const waUrl = buildWhatsAppUrl(waMessage);

  const formatPrice = (value: number) => {
    try {
      return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(value);
    } catch {
      return `${value} TL`;
    }
  };
  const displayPrice = izmirProduct.priceTRY ? formatPrice(izmirProduct.priceTRY) : null;

  const pageReviews = izmirProduct.reviews?.length
    ? izmirProduct.reviews.map((r) => ({
      name: r.authorName,
      date: r.datePublished,
      title: r.title,
      verified: !!r.verified,
      rating: r.ratingValue,
      text: r.body,
    }))
    : [];

  const avgRating =
    pageReviews.length > 0 ? pageReviews.reduce((s, r) => s + (Number.isFinite(r.rating) ? r.rating : 0), 0) / pageReviews.length : 0;

  const topSpecs = [
    { k: 'Stok', v: 'İzmir içi düzenli stok' },
    { k: 'Garanti', v: 'Kurumsal' },
    { k: 'Teslim', v: '1-3 iş günü' },
    { k: 'Kurulum', v: 'Ücretsiz kurulum desteği' },
    { k: 'Kalınlıklar', v: '10mm, 20mm, 30mm, 40mm, 50mm' },
    { k: 'Hizmet Bölgesi', v: 'İzmir geneli (Merkez + tüm ilçeler)' },
    { k: 'Uygulama', v: 'Yapıştırma / Mekanik tespit' },
    { k: 'Süre', v: '1 gün' },
  ];

  const breadcrumb = [
    { name: 'Anasayfa', item: toAbsoluteUrl('/') },
    { name: 'Ürünler', item: toAbsoluteUrl('/urunler') },
    { name: 'İzmir Bondex Sünger', item: url },
  ];

  const validFromStr = new Date().toISOString().split('T')[0];
  const priceValidUntil = new Date();
  priceValidUntil.setFullYear(priceValidUntil.getFullYear() + 1);
  const priceValidUntilStr = priceValidUntil.toISOString().split('T')[0];

  const schemaGraph: any[] = [
    {
      '@type': 'WebSite',
      '@id': `${toAbsoluteUrl('/')}#website`,
      name: 'Bondex Sünger',
      url: toAbsoluteUrl('/'),
      inLanguage: 'tr-TR',
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${toAbsoluteUrl('/')}urunler?q={search_term_string}`,
        },
        'query-input': 'required name=search_term_string',
      },
    },
    {
      '@type': 'Organization',
      '@id': `${toAbsoluteUrl('/')}#organization`,
      name: 'bariyerlibondexsunger.com',
      url: toAbsoluteUrl('/'),
      logo: {
        '@type': 'ImageObject',
        url: toAbsoluteUrl('/images/bondex-logo.webp'),
        width: 512,
        height: 512,
      },
      contactPoint: [
        {
          '@type': 'ContactPoint',
          telephone: '+90 216 519 34 11',
          contactType: 'customer service',
          areaServed: 'TR',
          availableLanguage: ['tr'],
        },
        {
          '@type': 'ContactPoint',
          telephone: '+90 546 919 11 34',
          contactType: 'sales',
          areaServed: 'TR',
          availableLanguage: ['tr'],
        },
      ],
      sameAs: [
        'https://www.facebook.com/profile.php?id=61575206163572',
        'https://x.com/KabinPods',
        'https://www.instagram.com/kabinpods/',
        'https://www.youtube.com/channel/UCKk3RjOabrJb8JQYK9BjWkw',
        'https://www.linkedin.com/company/tasar%C4%B1m-akustik-mimarl%C4%B1k',
      ],
    },
    {
      '@type': 'WebPage',
      '@id': `${url}#webpage`,
      url,
      name: title,
      description,
      isPartOf: { '@id': `${toAbsoluteUrl('/')}#website` },
      breadcrumb: { '@id': `${url}#breadcrumb` },
      inLanguage: 'tr-TR',
      datePublished: '2026-01-24',
      dateModified: new Date().toISOString().split('T')[0],
      primaryImageOfPage: {
        '@type': 'ImageObject',
        url: toAbsoluteUrl(izmirProduct.imagePath),
        width: 1200,
        height: 750,
      },
      about: [
        { '@type': 'Thing', name: 'Bondex Sünger' },
        { '@type': 'Thing', name: 'İzmir Bondex Sünger' },
        { '@type': 'Thing', name: 'ses yalıtım süngeri' },
        { '@type': 'Thing', name: 'akustik sünger' },
        { '@type': 'Thing', name: 'izolasyon süngeri' },
        { '@type': 'Thing', name: 'İzmir ses yalıtımı' },
      ],
      speakable: {
        '@type': 'SpeakableSpecification',
        cssSelector: ['.ProductSEO', '.ProductPage h1'],
      },
      mainEntity: { '@id': `${url}#product` },
    },
    {
      '@type': 'BreadcrumbList',
      '@id': `${url}#breadcrumb`,
      itemListElement: breadcrumb.map((b, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        name: b.name,
        item: b.item,
      })),
    },
    {
      '@type': 'Product',
      '@id': `${url}#product`,
      name: izmirProduct.name,
      image: [
        {
          '@type': 'ImageObject',
          url: toAbsoluteUrl(izmirProduct.imagePath),
          width: 1200,
          height: 750,
        },
      ],
      description: izmirProduct.description,
      brand: { '@type': 'Brand', name: 'Bondex' },
      category: izmirProduct.category,
      url,
      sku: izmirProduct.sku,
      mpn: izmirProduct.mpn,
      productID: izmirProduct.sku || izmirProduct.mpn,
      material: 'Rebonded Süngerpan',
      audienceType: 'Business',
      additionalProperty: [
        { '@type': 'PropertyValue', name: 'Kategori', value: izmirProduct.category },
        { '@type': 'PropertyValue', name: 'Tip', value: 'Rebonded Süngerpan' },
        { '@type': 'PropertyValue', name: 'Hizmet Bölgesi', value: 'İzmir' },
      ],
      offers: {
        '@type': 'Offer',
        url,
        priceCurrency: 'TRY',
        price: String(izmirProduct.priceTRY),
        validFrom: validFromStr,
        priceValidUntil: priceValidUntilStr,
        availability: 'https://schema.org/InStock',
        itemCondition: 'https://schema.org/NewCondition',
        seller: { '@id': `${toAbsoluteUrl('/')}#organization` },
        unitText: 'm²',
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          price: String(izmirProduct.priceTRY),
          priceCurrency: 'TRY',
          unitCode: 'MTK',
          unitText: 'm²',
        },
        hasMerchantReturnPolicy: { '@id': `${url}#merchantReturnPolicy` },
        shippingDetails: { '@id': `${url}#shippingDetails` },
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: avgRating.toFixed(1),
        reviewCount: pageReviews.length,
        bestRating: '5',
        worstRating: '1',
      },
      review: izmirProduct.reviews?.map((r) => ({
        '@type': 'Review',
        author: { '@type': 'Person', name: r.authorName },
        datePublished: r.datePublished,
        reviewBody: r.body,
        headline: r.title,
        ...(r.verified && { reviewAspect: 'Verification' }),
        reviewRating: {
          '@type': 'Rating',
          ratingValue: String(r.ratingValue),
          bestRating: '5',
          worstRating: '1',
        },
      })),
    },
    {
      '@type': 'MerchantReturnPolicy',
      '@id': `${url}#merchantReturnPolicy`,
      applicableCountry: { '@type': 'Country', name: 'TR' },
      returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
      merchantReturnDays: 14,
      returnMethod: 'https://schema.org/ReturnByMail',
      returnFees: 'https://schema.org/FreeReturn',
    },
    {
      '@type': 'OfferShippingDetails',
      '@id': `${url}#shippingDetails`,
      shippingRate: { '@type': 'MonetaryAmount', value: '0', currency: 'TRY' },
      shippingDestination: {
        '@type': 'DefinedRegion',
        addressCountry: { '@type': 'Country', name: 'TR' },
      },
      deliveryTime: {
        '@type': 'ShippingDeliveryTime',
        handlingTime: { '@type': 'QuantitativeValue', minValue: 1, maxValue: 3, unitCode: 'DAY' },
        transitTime: { '@type': 'QuantitativeValue', minValue: 1, maxValue: 5, unitCode: 'DAY' },
        businessDays: {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        },
        cutoffTime: '14:00',
      },
    },
    {
      '@type': 'FAQPage',
      '@id': `${url}#faq`,
      mainEntity: izmirProduct.faqs?.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer },
      })),
    },
  ];

  const schema = {
    '@context': 'https://schema.org',
    '@graph': schemaGraph,
  };

  const tabItems = [
    {
      key: 'aciklama',
      label: 'Açıklama',
      content: (
        <div className={styles.seoWrap}>
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>İzmir Bondex Sünger Nedir?</h2>
            <h3 className={styles.seoSubtitle}>Rebonded Süngerpan Yapısı ve Akustik Etkisi</h3>
            <p className={styles.seoText}>
              <strong>İzmir bondex sünger</strong>, geri dönüştürülmüş sünger granüllerinin yüksek basınçla sıkıştırılmasıyla üretilen
              rebonded süngerpan esaslı bir yalıtım malzemesidir. Malzemenin yoğun yapısı, özellikle darbe kaynaklı titreşimlerin yapı
              elemanlarında yayılımını azaltmaya yardımcı olur. Bu özellik, apartman dairelerinde üst kattan gelen adım sesi, teknik hacimlerde
              makine titreşimi ve ofis bölmelerinde komşu hacim sesi gibi çok farklı problemlerde aynı ürün ailesiyle çözüm kurmayı mümkün kılar.
              İzmir gibi hem yoğun konut stoğu hem de ticari yapı çeşitliliği yüksek şehirlerde, tek tip bir çözüm yerine doğru kalınlık ve doğru
              katman kurgusu ile ilerlemek gerekir.
            </p>
            <p className={styles.seoText}>
              Uygulama tarafında en kritik nokta, yalnızca ürün seçimi değil, ürünün hangi yüzeyde hangi yardımcı katman ile kullanıldığıdır.
              Örneğin yalnızca duvara malzeme yapıştırmak her projede aynı sonucu vermez; ara profiller, çift kat plaka, derz bantları, elastik
              bağlantı elemanları ve kenar dönüş detayları birlikte düşünülmelidir. Bu sebeple İzmir içi projelerde metraj kadar uygulama detayı
              da önemlidir. Keşif sırasında yüzey tipleri, mevcut kaplama durumu, tesisat geçişleri ve hedeflenen konfor düzeyi netleştirilirse
              hem maliyet yönetimi hem de performans beklentisi daha gerçekçi biçimde planlanır.
            </p>
            <h3 className={styles.seoSubtitle}>İzmir Şehir Dinamiğine Uyumlu Çözüm</h3>
            <p className={styles.seoText}>
              İzmir'in ilçeleri arasında yapı karakteri ciddi farklılık gösterir. Karşıyaka ve Bayraklı hattında yüksek katlı yeni proje
              yoğunluğu varken, Konak ve çevresinde renovasyon ihtiyacı daha fazladır; Bornova, Buca ve Gaziemir tarafında ise konut ve ticari
              kullanım iç içe geçtiği için karma ihtiyaçlar öne çıkar. Bu yüzden her projeye aynı kalınlığı önermek yerine kullanım senaryosuna
              göre ürün seçimi yapılır. Standart ürün ailesi için{' '}
              <Link to="/bondex-sunger" rel="follow">bondex sünger</Link>, ileri performans için{' '}
              <Link to="/bariyerli-bondex-sunger" rel="follow">bariyerli bondex</Link> ve en üst hedefler için{' '}
              <Link to="/cift-bariyerli-bondex-sunger" rel="follow">çift bariyerli bondex</Link> seçenekleri birbiriyle kıyaslanarak sunulur.
            </p>
            <p className={styles.seoText}>
              İzmir'e özel planlamada yalnızca akustik performans değil, uygulama pratikliği de önemlidir. Aktif kullanılan ofislerde veya
              yaşamın devam ettiği konutlarda iş programı doğru yönetilmezse uygulama kalitesi düşebilir. Bu nedenle süreç etaplara bölünür,
              her etap sonunda yüzey ve birleşim detayları kontrol edilerek bir sonraki aşamaya geçilir. İlçe bazlı erişim koşulları ve teslimat
              saatleri önceden planlandığında saha kayıpları azalır, toplam süre kısalır ve sonuç daha öngörülebilir olur.
            </p>
          </section>

          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>İzmir'de Bondex Sünger Nerelerde Kullanılır?</h2>
            <h3 className={styles.seoSubtitle}>Konut, Site ve Apartman Uygulamaları</h3>
            <p className={styles.seoText}>
              Konut projelerinde en sık karşılaşılan şikayetler; katlar arası darbe sesi, duvar geçişlerinde konuşma sesi ve ortak alan kaynaklı
              gürültüdür. İzmir'deki site yaşamının arttığı bölgelerde bu ihtiyaçlar daha belirgin hale gelmiştir. Şap altı uygulamalarda darbe
              sesi kontrolü için uygun kalınlık seçilirken, duvar sistemlerinde ses geçişini azaltmak amacıyla ek katmanlı çözümler devreye alınır.
              İnce yapı toleransı gereken alanlarda <Link to="/bondex-sunger-10mm" rel="follow">10mm</Link> ve{' '}
              <Link to="/bondex-sunger-20mm" rel="follow">20mm</Link> ürünleri tercih edilir; daha güçlü performans gereken senaryolarda{' '}
              <Link to="/bondex-sunger-30mm" rel="follow">30mm</Link>, <Link to="/bondex-sunger-40mm" rel="follow">40mm</Link> ve gerekli
              durumlarda <Link to="/bondex-sunger-50mm" rel="follow">50mm</Link> kalınlıklar değerlendirilir.
            </p>
            <p className={styles.seoText}>
              Apartman yenilemelerinde kullanıcıların en büyük beklentisi, daire içinde yaşamı durdurmadan uygulamanın hızlı tamamlanmasıdır.
              Bu noktada doğru ürün kadar lojistik planlama da önemlidir. Malzemenin katlara taşınması, kesimlerin gürültüsüz saatlerde yapılması,
              ortak alan düzeninin korunması ve etaplı uygulama takvimi; projenin kalite algısını doğrudan etkiler. İzmir içinde yapılan
              projelerde bu planlama, şantiye veya apartman yönetimiyle birlikte yürütülerek sürecin sorunsuz ilerlemesi hedeflenir.
            </p>
            <h3 className={styles.seoSubtitle}>Ofis, Stüdyo ve Ticari Hacimler</h3>
            <p className={styles.seoText}>
              Açık ofislerde yankı, telefon görüşmesi karmaşası ve toplantı odalarına ses sızıntısı temel problemler arasındadır. Özellikle
              Alsancak, Bayraklı ve Konak hattındaki ofis projelerinde ses kontrolü çalışan verimliliği açısından doğrudan önem taşır. Bondex
              sünger, bölme duvar ve teknik oda detaylarında doğru katmanlarla kullanıldığında ortamın akustik konforunu yükseltir. Stüdyo,
              podcast odası ve içerik üretim alanlarında ise hem iç akustiği dengelemek hem de komşu hacimlere ses geçişini azaltmak için
              katman kombinasyonları daha hassas şekilde planlanır.
            </p>
            <p className={styles.seoText}>
              Ticari hacimlerde tek hedef maksimum sessizlik değil, işlevsel ve sürdürülebilir bir akustik dengedir. Bu nedenle sadece kalın
              malzeme kullanmak yerine, ses kaynağına yakın noktaların doğru tespiti yapılır; ardından yerel güçlendirme ve genel katman
              stratejisi birlikte uygulanır. Gerektiğinde bariyerli sistemlerle hibrit kurgu kurularak hem darbe hem hava kaynaklı ses için
              aynı anda çözüm üretilir.
            </p>
            <p className={styles.seoText}>
              Ticari projelerde ses kontrolü kadar mekan estetiği de önem taşır. Bu yüzden uygulama sonrası gelecek kaplama sistemi, kullanım
              yoğunluğu ve bakım gereksinimi birlikte değerlendirilir. Doğru detaylandırma yapıldığında yalnızca teknik olarak güçlü bir
              sonuç değil, aynı zamanda uzun süre korunabilen düzenli bir görünüm elde edilir. İzmir'de otel, klinik ve eğitim yapılarında
              bu yaklaşım, kullanıcı memnuniyetini doğrudan artıran güçlü bir fark yaratır.
            </p>
            <ul className={styles.seoList}>
              <li>İzmir konut projelerinde şap altı darbe sesi kontrolü ve duvar geçişlerinde konuşma sesi azaltımı</li>
              <li>Ofislerde toplantı odası, call odası ve teknik odalarda komşu hacim ses transferini azaltan katman çözümleri</li>
              <li>Stüdyo ve podcast odalarında kayıt kalitesini destekleyen kontrollü akustik düzenleme</li>
              <li>Makine dairesi, jeneratör hacmi ve teknik merkezlerde titreşim sönümleme odaklı uygulamalar</li>
              <li>Kapı, şaft ve geçiş detaylarında noktasal ses kaçaklarını azaltan kombine sistemler</li>
              <li>Renovasyon projelerinde düşük kalınlıkla işlevsel performans sağlayan optimize ürün seçimi</li>
            </ul>
          </section>
        </div>
      ),
    },
    {
      key: 'teknik',
      label: 'Teknik Özellikler',
      content: (
        <div className={styles.seoWrap}>
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Teknik Özellikler ve Performans Kriterleri</h2>
            <h3 className={styles.seoSubtitle}>Yoğunluk, Kalınlık ve Enerji Sönümleme İlişkisi</h3>
            <p className={styles.seoText}>
              İzmir bondex sünger çözümlerinde performansı belirleyen üç temel parametre vardır: yoğunluk, kalınlık ve katman kombinasyonu.
              Rebonded yapı sayesinde malzeme, darbe enerjisini homojen biçimde dağıtarak titreşimin taşıyıcı elemanlar boyunca yayılımını
              azaltmaya yardımcı olur. Yoğunluk artışı tek başına her zaman yeterli değildir; uygulama yüzeyinin tipi, bağlantı detayları ve
              üst katman malzemeleri de sonuca doğrudan etki eder. Bu nedenle teknik planlama yapılırken ürün seçimi yüzey mühendisliğiyle
              birlikte ele alınmalıdır.
            </p>
            <p className={styles.seoText}>
              İzmir'deki projelerde en sık uygulanan kalınlıklar 10mm, 20mm, 30mm, 40mm ve 50mm'dir. Düşük katman toleransı olan renovasyon
              işlerinde 10-20mm, daha agresif gürültü yükünde 30-50mm aralığı tercih edilir. Eğer hedef yalnızca darbe sesini azaltmak değil,
              hava kaynaklı ses geçişini de baskılamak ise bariyerli katmanlarla desteklenen hibrit kurgu daha doğru sonuç verir.
            </p>
            <h3 className={styles.seoSubtitle}>Uygulama Yüzeyleri ve Detay Prensipleri</h3>
            <p className={styles.seoText}>
              Malzeme; betonarme döşeme, alçıpan bölme duvar, çelik profil sistem, ahşap panel altı ve teknik hacimlerdeki karma yüzeylerde
              kullanılabilir. Ancak her yüzey aynı yapıştırıcı, aynı sabitleme tekniği veya aynı ek detayını kaldırmaz. Şap altı uygulamada
              kenar bantlarının sürekliliği, duvar uygulamasında profil temas noktalarının izolasyonu, kapı detayında çevresel sızdırmazlık
              ve makine odalarında kaynak noktasına yakın konumlandırma gibi kriterler, teorik performansı sahada gerçek sonuca dönüştürür.
            </p>
            <p className={styles.seoText}>
              Yanlış uygulanan çok kalın bir ürün, doğru uygulanmış orta kalınlıktaki bir sistemden daha zayıf sonuç verebilir. Bu yüzden keşif
              sırasında yalnızca "kaç milimetre" sorusuna değil, "hangi katmanda, hangi detayla, hangi hedefe göre" sorularına da net cevap
              oluşturmak gerekir. Tüm ürün varyasyonları ve teknik karşılaştırmalar için{' '}
              <Link to="/urunler" rel="follow">ürünler</Link> sayfası üzerinden model bazlı değerlendirme yapılabilir.
            </p>
            <h3 className={styles.seoSubtitle}>Performans Hedefi ve Maliyet Dengesi</h3>
            <p className={styles.seoText}>
              İyi bir akustik proje sadece yüksek performans değil, sürdürülebilir bir maliyet-performans dengesi de sunmalıdır. İzmir'de
              konut dönüşümü, ticari yenileme ve yeni inşa projelerinde bütçe kalemleri farklı ilerlediği için önerilen sistem de değişir.
              Gereğinden kalın ürün kullanımı maliyeti yükseltirken, yetersiz kalınlık ise beklenen sonucu düşürür. Bu nedenle her proje için
              hedeflenen konfor düzeyi, kullanım tipi ve uygulama süresi birlikte değerlendirilerek optimum senaryo oluşturulur.
            </p>
            <p className={styles.seoText}>
              Teknik kararların ölçülebilir hedeflerle desteklenmesi, proje sonunda memnuniyeti ciddi oranda yükseltir. Konutta hedef, gece
              saatlerinde komşu kaynaklı darbe sesini azaltmak olabilir; ofiste ise toplantı odalarında konuşma gizliliğini artırmak ve açık
              alandaki yankıyı düşürmek öne çıkar. Hedef net tanımlandığında sistem seçimi de netleşir. Bu yaklaşım gereksiz malzeme kullanımını
              engeller, yatırımın karşılığını daha görünür hale getirir ve uygulama sonrası beklenti yönetimini kolaylaştırır.
            </p>
          </section>
        </div>
      ),
    },
    {
      key: 'kullanim',
      label: 'Kullanım Alanları',
      content: (
        <div className={styles.seoWrap}>
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>İzmir'de Sık Uygulanan Senaryolar</h2>
            <h3 className={styles.seoSubtitle}>Yapı Yenileme Projeleri</h3>
            <p className={styles.seoText}>
              İzmir'de yenileme projelerinde en önemli kısıt, mevcut yapının korunması ve kullanımın tamamen durdurulmamasıdır. Bu nedenle
              ürün seçimi kadar uygulama sırası da önem taşır. Önce kritik kaçak noktaları tespit edilir; ardından zemin, duvar veya teknik
              oda detayları etap etap ele alınır. Bu yaklaşım hem maliyeti kontrol altında tutar hem de kullanıcıların gündelik yaşamını
              minimum düzeyde etkiler. Özellikle eski yapı stokunda karşılaşılan düzensiz yüzeyler için yerinde ölçüm ve doğru kesim planı,
              uygulama kalitesinin temel belirleyicisidir.
            </p>
            <h3 className={styles.seoSubtitle}>Yeni Konut ve Karma Kullanım Projeleri</h3>
            <p className={styles.seoText}>
              Yeni projelerde en avantajlı dönem, kaba inşaat ve ince işçilik arasında kalan planlama sürecidir. Bu evrede yapılan doğru
              katman tasarımı, sonradan yapılacak tadilat ihtiyacını azaltır. İzmir'de özellikle yeni gelişen bölgelerde, konut ve ofis
              fonksiyonlarının birlikte bulunduğu yapılarda hem kullanıcı konforu hem de kiralama değeri açısından akustik performans önemli
              bir kriter haline gelmiştir. Bondex tabanlı çözümler, proje disiplinleriyle uyumlu biçimde entegre edildiğinde uzun vadeli
              memnuniyet sağlayan bir altyapı oluşturur.
            </p>
            <h3 className={styles.seoSubtitle}>Endüstriyel ve Teknik Hacimler</h3>
            <p className={styles.seoText}>
              Çiğli Atatürk Organize Sanayi Bölgesi, Kemalpaşa OSB ve liman çevresindeki ticari/üretim tesislerinde ses ve titreşim yönetimi
              yalnızca konfor değil, iş güvenliği ve operasyon sürekliliği açısından da önemlidir. Makine odası, kompresör alanı, jeneratör
              hacmi, fan ve pompa odaları gibi noktalarda kaynak sesi doğru tespit edilmeden yapılan uygulamalar yetersiz kalabilir. Bu nedenle
              proje öncesi teknik analiz yapılarak kaynağa yakın ve taşıyıcıya geçişi azaltan bir katman sistemi kurgulanır.
            </p>
            <p className={styles.seoText}>
              Endüstriyel senaryolarda tek bir ürün yerine çok katmanlı yaklaşım daha iyi sonuç verir. Bondex sünger, bariyerli katmanlar ve
              uygun mekanik detaylar birlikte planlandığında hem düşük frekanslı titreşim hem de hava kaynaklı ses geçişlerinde dengeli bir
              iyileştirme elde edilir. Uygulama sonrasında bakım erişimi, ekipman servis alanı ve güvenlik mesafeleri korunarak sürdürülebilir
              bir sistem elde edilmesi hedeflenir.
            </p>
            <p className={styles.seoText}>
              İzmir'deki üretim tesislerinde sık görülen bir diğer ihtiyaç, farklı çalışma temposuna sahip bölümlerin aynı yapı içinde
              bulunmasıdır. Üretim alanı, idari ofis ve sosyal alanlar birbirine yakınsa bölgesel katman stratejisi gerekir. Gürültü kaynağına
              yakın noktalarda daha güçlü sistem, destek alanlarında ise optimize kalınlık kullanılarak hem bütçe hem performans dengesi
              korunur. Böylece günlük operasyon aksamadan ilerlerken çalışan konforu da sürdürülebilir hale gelir.
            </p>
          </section>
        </div>
      ),
    },
    {
      key: 'opsiyonel',
      label: 'Opsiyonel',
      content: (
        <div className={styles.seoWrap}>
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Teslimat, Uygulama Desteği ve Şehir Planlaması</h2>
            <h3 className={styles.seoSubtitle}>İzmir İçi Lojistik ve Termin Yönetimi</h3>
            <p className={styles.seoText}>
              İzmir merkez, Karşıyaka, Bornova, Bayraklı, Konak, Buca, Gaziemir, Narlıdere, Menemen, Torbalı ve çevre ilçelere düzenli
              sevkiyat planlanır. Sevkiyat modelinde şantiye erişimi, kat yüksekliği, asansör kullanımı, boşaltma alanı ve depolama koşulu
              dikkate alınır. Proje tipine göre paletli, rulo veya kesimli formatta teslimat yapılabilir. Büyük metrajlı işlerde etaplı
              gönderim planı uygulanarak sahadaki stok yükü azaltılır, uygulama ekibinin çalışma akışı hızlandırılır.
            </p>
            <p className={styles.seoText}>
              Uygulama tarihinin belirlenmesinde yalnızca malzeme hazır olması yeterli değildir. Yüzey hazırlığı, diğer disiplinlerin iş
              bitişi, hava koşulları ve bina yönetimi izinleri de termin planını etkiler. Bu nedenle teklif aşamasında zaman çizelgesi net
              kurulduğunda gecikme riski azalır. İzmir'de özellikle yoğun merkez ilçelerde trafik ve erişim planlaması önden yapıldığında
              saha operasyonu daha verimli ilerler.
            </p>
            <h3 className={styles.seoSubtitle}>Teknik Destek ve Uygulama Sonrası Süreç</h3>
            <p className={styles.seoText}>
              Teknik destek süreci, yalnızca ürün satışıyla sınırlı değildir. Proje başlangıcında ihtiyaç analizi, uygulama sırasında detay
              kontrolü ve uygulama sonrasında performans değerlendirmesi birlikte ele alınır. Böylece kullanıcı, hangi kalınlığın neden
              seçildiğini, hangi detayın hangi sorunu hedeflediğini net biçimde görür. Bu şeffaf yaklaşım, özellikle birden fazla daire veya
              çok katlı ofis projelerinde karar alma hızını artırır.
            </p>
            <p className={styles.seoText}>
              İzmir dışına yapılacak projelerde de aynı teknik disiplin korunur. İstanbul, Ankara, Bursa, Antalya, Kocaeli ve çevre şehirler
              için teklif, sevkiyat ve uygulama koordinasyonu proje ölçeğine göre planlanır. Eğer birden fazla şehirde paralel proje yürütülüyorsa,
              ürün standardizasyonu ve termin yönetimi tek merkezden takip edilerek kalite sürekliliği sağlanır.
            </p>
            <h3 className={styles.seoSubtitle}>Neden İzmir'e Özel İçerik ve Çözüm?</h3>
            <p className={styles.seoText}>
              Her şehirde ses problemi aynı görünse de yapı stoku, kullanıcı beklentisi ve uygulama koşulları değişir. İzmir'e özel içerik ve
              çözüm yaklaşımı; ilçe bazlı yapı tiplerini, kullanım senaryolarını ve yerel lojistik gerçeklerini dikkate aldığı için daha doğru
              sonuç üretir. Amaç yalnızca ürün satmak değil, kullanıcıya ölçülebilir ve sürdürülebilir bir akustik konfor sağlamaktır. Eğer
              projeniz için net bir öneri istiyorsanız, metraj ve kullanım detayını paylaşarak kısa sürede teknik teklif alabilirsiniz.
            </p>
            <p className={styles.seoText}>
              Şehir odaklı içerik yaklaşımı SEO tarafında da güçlü katkı sağlar. İzmir'e özgü arama niyetleri, ilçe bazlı kullanım örnekleri
              ve sahadan gelen sık sorular aynı sayfada toplandığında kullanıcı aradığını daha hızlı bulur. Bu durum yalnızca organik görünürlük
              değil, dönüşüm kalitesi açısından da değer üretir. Sonuç olarak şehir bazlı kapsamlı içerik, hem teknik güven hem de ticari
              verimlilik açısından en sağlıklı yöntemdir.
            </p>
          </section>
        </div>
      ),
    },
  ];

  return (
    <main className="container ProductPage">
      <JsonLd data={schema} />

      <nav className={styles.breadcrumb} aria-label="Breadcrumb">
        <Link to="/">Anasayfa</Link>
        <span aria-hidden="true">/</span>
        <Link to="/urunler">Ürünler</Link>
        <span aria-hidden="true">/</span>
        <span aria-current="page">İzmir Bondex Sünger</span>
      </nav>

      <div className={styles.heroGrid}>
        <div className={styles.media}>
          <img
            className={styles.img}
            src={izmirProduct.imagePath}
            alt="İzmir bondex sünger uygulaması"
            loading="eager"
            width="600"
            height="400"
          />
        </div>

        <section className={`ProductSEO card ${styles.panel}`}>
          <h1 className={styles.h1}>İzmir Bondex Sünger</h1>
          <p className={styles.modelLine}>
            <span className={styles.modelLabel}>Model:</span> BDX-IZM | İzmir İçi Projeler
          </p>
          <p className={styles.meta}>
            İzmir'de <strong>bondex sünger</strong> arayan konut, ofis, stüdyo ve endüstriyel projeler için; stoktan hızlı sevkiyat,
            keşif desteği ve teknik danışmanlığı tek noktadan sunuyoruz. Yüksek yoğunluklu <strong>rebonded süngerpan</strong> yapısı,
            darbe ve titreşim kaynaklı gürültüleri azaltmaya yardımcı olurken proje hedefinize uygun katman kurgusu oluşturuyoruz.
          </p>

          {pageReviews.length > 0 && (
            <div className={styles.ratingRow}>
              <div className={styles.stars}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <IconStar key={star} style={{ opacity: star <= Math.round(avgRating) ? 1 : 0.3 }} />
                ))}
              </div>
              <span className={styles.ratingText}>
                {avgRating.toFixed(1)} ({pageReviews.length} değerlendirme)
              </span>
            </div>
          )}

          {displayPrice && (
            <div className={styles.priceRow}>
              <span className={styles.price}>{displayPrice}</span>
              <span className={styles.priceUnit}>/ m²'den başlayan</span>
            </div>
          )}

          <div className={styles.badges}>
            <span className="chip">Bondex Sünger</span>
            <span className="chip">İzmir içi hızlı teslimat</span>
            <span className="chip">Keşif ve uygulama desteği</span>
          </div>

          <div className={styles.specGrid} aria-label="İzmir için özet bilgiler">
            {topSpecs.map((spec) => (
              <div key={spec.k} className={styles.specItem}>
                <div className={styles.specK}>{spec.k}</div>
                <div className={styles.specV}>{spec.v}</div>
              </div>
            ))}
          </div>

          <div className={styles.ctaRow}>
            <a href={waUrl} target="_blank" rel="noopener noreferrer" className={`btn btn-primary ${styles.ctaBtn}`}>
              <IconWhatsApp />
              <span>WhatsApp ile Teklif Al</span>
            </a>
            <a href={`tel:${SALES_PHONE_READABLE.replace(/\s/g, '')}`} className={`btn btn-outline ${styles.ctaBtn}`}>
              <IconPhone />
              <span>{SALES_PHONE_READABLE}</span>
            </a>
          </div>
        </section>
      </div>

      <div className={styles.contentGrid}>
        <section>
          <div className={`card ${styles.sectionCard}`} style={{ padding: 0 }}>
            <Tabs items={tabItems} defaultKey="aciklama" />
          </div>
        </section>
      </div>

      {pageReviews.length > 0 && (
        <section className={styles.reviewSection}>
          <div className={styles.reviewHeader}>
            <h2 className={styles.reviewTitle}>Müşteri Değerlendirmeleri</h2>
            <div className={styles.reviewUnderline} aria-hidden="true" />
          </div>
          <div className={styles.reviewGrid}>
            {pageReviews.map((review, idx) => (
              <div key={idx} className={styles.reviewCard}>
                <div className={styles.reviewTop}>
                  <div className={styles.reviewStars}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <IconStar key={star} style={{ opacity: star <= review.rating ? 1 : 0.3 }} />
                    ))}
                  </div>
                  {review.verified && <span className={styles.verifiedBadge}>✓ Doğrulanmış</span>}
                </div>
                <h3 className={styles.reviewName}>{review.title}</h3>
                <p className={styles.reviewText}>{review.text}</p>
                <div className={styles.reviewFooter}>
                  <span className={styles.reviewAuthor}>{review.name}</span>
                  <span className={styles.reviewSeparator}>•</span>
                  <span className={styles.reviewDate}>{review.date}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {izmirProduct.faqs?.length ? (
        <section className={styles.faqSection}>
          <div className={styles.faqHeader}>
            <h2 className={styles.faqTitle}>İzmir Bondex Sünger – Sıkça Sorulan Sorular</h2>
            <div className={styles.faqUnderline} aria-hidden="true" />
          </div>
          <div className={styles.faqCard}>
            <Accordion items={izmirProduct.faqs} variant="seo" />
          </div>
        </section>
      ) : null}

      {relatedBondex.length ? (
        <section className={styles.relatedSection}>
          <div className={styles.relatedHeader}>
            <h2 className={styles.relatedTitle}>Diğer Bondex Sünger Modelleri</h2>
            <div className={styles.relatedUnderline} aria-hidden="true" />
          </div>
          <div className={styles.relatedGrid}>
            {relatedBondex.map((p) => (
              <div key={p.slug} className={styles.relatedItem}>
                <ProductCard product={p} />
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </main>
  );
}
